import { Injectable } from '@angular/core';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { GlobalParticipant } from 'app/projects/user/src/lib/models/participant.model';
import { User } from 'app/projects/user/src/lib/models/user';
import { firstValueFrom, Observable, race, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
    AddUsersToTeamsAsLeaders,
    AddUsersToTeamsAsLeadersFailure,
    AddUsersToTeamsAsLeadersSuccess,
    AddUsersToTeamsAsMembers,
    AddUsersToTeamsAsMembersFailure,
    AddUsersToTeamsAsMembersSuccess,
    AddUsersToTeamsAsWatchers,
    AddUsersToTeamsAsWatchersFailure,
    AddUsersToTeamsAsWatchersSuccess,
    CreateTeam,
    CreateTeamFailure,
    CreateTeamSuccess,
    DeleteTeam,
    DeleteTeamFailure,
    DeleteTeamSuccess,
    FetchRootTeams,
    FetchSubTeamWithSubteamsProgress,
    FetchSubTeamWithSubteamsProgressFailure,
    FetchSubTeamWithSubteamsProgressSuccess,
    FetchTeamChildren,
    FetchTeamChildrenFailure,
    FetchTeamChildrenSuccess,
    FetchTeamLeaders,
    FetchTeamLeadersFailure,
    FetchTeamLeadersSuccess,
    FetchTeamLeaderTeams,
    FetchTeamLeaderTeamsFailure,
    FetchTeamLeaderTeamsSuccess,
    FetchTeamMembers,
    FetchTeamMembersFailure,
    FetchTeamMembersSuccess,
    FetchTeamMembersUsers,
    FetchTeamMembersUsersFailure,
    FetchTeamMembersUsersSuccess,
    FetchTeamParent,
    FetchTeamParentFailure,
    FetchTeamParentSuccess,
    FetchTeams,
    FetchTeamsFailure,
    FetchTeamsSuccess,
    FetchTeamSubTeams,
    FetchTeamSubTeamsFailure,
    FetchTeamSubTeamsSuccess,
    FetchTeamUsers,
    FetchTeamUsersFailure,
    FetchTeamUsersSuccess,
    FetchTeamWatchers,
    FetchTeamWatchersFailure,
    FetchTeamWatchersSuccess,
    FetchTeamWithSubTeamsProgress,
    FetchTeamWithSubTeamsProgressFailure,
    FetchTeamWithSubTeamsProgressSuccess,
    FetchUserTeams,
    FetchUserTeamsFailure,
    FetchUserTeamsSuccess,
    FetchUserTeamsV1,
    FetchUserTeamsV1Failure,
    FetchUserTeamsV1Success,
    RemoveTeamLeaders,
    RemoveTeamLeadersFailure,
    RemoveTeamLeadersSuccess,
    RemoveTeamMembers,
    RemoveTeamMembersFailure,
    RemoveTeamMembersSuccess,
    RemoveTeamWatchers,
    RemoveTeamWatchersFailure,
    RemoveTeamWatchersSuccess,
    SearchTeams,
    SearchTeamsFailure,
    SearchTeamsSuccess,
    StopWatchingTeam,
    StopWatchingTeamFailure,
    StopWatchingTeamSuccess,
    UnsetTeam,
    UpdateTeam,
    UpdateTeamFailure,
    UpdateTeamSuccess,
} from '../actions/index';
import { IAddTeamLeadersActionPayload } from '../models/add-team-leaders-action.action.payload';
import { IAddTeamMembersActionPayload } from '../models/add-team-members-action.action.payload';
import { IAddTeamWatchersActionPayload } from '../models/add-team-watchers-action.action.payload';
import { IRemoveTeamLeadersActionPayload } from '../models/remove-team-leaders-action.action.payload';
import { IRemoveTeamMembersActionPayload } from '../models/remove-team-members-action.action.payload';
import { IRemoveTeamWatchersActionPayload } from '../models/remove-team-watchers-action.action.payload';
import { IEntityNode } from 'app/projects/shared/src/lib/interfaces/entity-node.interface';
import { TeamMember } from '../models/team-member.model';
import { TeamTree } from '../models/team-tree.model';
import { Team } from '../models/team.model';
import { TeamState } from '../models/team.state';
import { TeamsState } from '../models/teams.state';
import { UserTeam } from '../models/user-team.model';

@Injectable()
export class TeamService {
    @Select(TeamState.getEntity)
    team$: Observable<Team>;

    @Select(TeamState.getEntities)
    teams$: Observable<Team[]>;

    @Select(TeamState.getRootTeams)
    rootTeams$: Observable<Team[]>;

    @Select(TeamsState.getTeamMembers)
    teamMembers$: Observable<TeamMember[]>;

    @Select(TeamsState.getTeamMembersTotalCount)
    teamMembersTotalCount$: Observable<number>;

    @Select(TeamsState.getTeamMembersLoading)
    teamMembersLoading$: Observable<boolean>;

    @Select(TeamsState.getTeamLeaders)
    teamLeaders$: Observable<User[]>;

    @Select(TeamsState.getTeamWatchers)
    teamWatchers$: Observable<User[]>;

    @Select(TeamsState.getUserTeams)
    userTeams$: Observable<UserTeam[]>;

    @Select(TeamsState.getTeamSubTeams)
    teamSubTeams$: Observable<Team[]>;

    @Select(TeamsState.getTeamSubTeamsTotalCount)
    teamSubTeamsTotalCount$: Observable<number>;

    @Select(TeamsState.getTeamSubTeamsLoading)
    teamSubTeamsLoading$: Observable<boolean>;

    @Select(TeamsState.getUserProgress)
    userProgress$: Observable<TeamMember>;

    @Select(TeamsState.getUserOtherTeams)
    userOtherTeams$: Observable<Team[]>;

    private _newTeamSubject = new Subject<Team>();
    public newTeamSubject$ = this._newTeamSubject.asObservable();

    private _addChildrenSubject = new Subject<Team>();
    public addChildrenSubject$ = this._addChildrenSubject.asObservable();

    private _teamRemovedSubject = new Subject<Team>();
    public teamRemovedSubject$ = this._teamRemovedSubject.asObservable();

    private _refreshTeamTrainingResponsiblesSubject = new Subject<string>();
    public readonly refreshTeamTrainingResponsibles$ = this._refreshTeamTrainingResponsiblesSubject.asObservable();

    private _collapseAllTeamsSubject = new Subject<void>();
    public collapseAllTeamsSubject$ = this._collapseAllTeamsSubject.asObservable();

    constructor(private _store: Store, private _action$: Actions, private _actionHelper: NgxsActionHelper) {}

    requestRefreshTeamTrainingResponsibles(teamId: string): void {
        this._refreshTeamTrainingResponsiblesSubject.next(teamId);
    }

    getTeams(): Team[] {
        return this._store.selectSnapshot(TeamsState.getFilteredEntities);
    }

    getRootTeams(): Team[] {
        return this._store.selectSnapshot(TeamState.getRootTeams);
    }

    getCurrentTeam(): Team {
        return this._store.selectSnapshot(TeamState.getEntity);
    }

    getTeamLeaderTeams(): Team[] {
        return this._store.selectSnapshot(TeamsState.getTeamLeaderTeams);
    }

    getTeamSubTeams(): Team[] {
        return this._store.selectSnapshot(TeamsState.getTeamSubTeams);
    }

    getTeamMemberTeams(): Team[] {
        return this._store.selectSnapshot(TeamsState.getTeamMemberTeams);
    }

    async fetchTeams(): Promise<boolean> {
        this._store.dispatch(new FetchTeams());

        return await this._actionHelper.race(FetchTeamsSuccess, FetchTeamsFailure).toPromise();
    }

    async fetchRootTeams(): Promise<boolean> {
        this._store.dispatch(new FetchRootTeams());

        return await this._actionHelper.race(FetchTeamsSuccess, FetchTeamsFailure).toPromise();
    }

    async fetchTeamChildren(id: string): Promise<boolean> {
        this._store.dispatch(new FetchTeamChildren(id));

        this._action$.pipe(ofActionDispatched(FetchTeamChildrenSuccess), take(1)).subscribe((action: FetchTeamChildrenSuccess) => {
            action.payload.forEach((team) => this._addChildrenSubject.next(team));
        });

        return await this._actionHelper.race(FetchTeamChildrenSuccess, FetchTeamChildrenFailure).toPromise();
    }

    async fetchParentChildren(id: string): Promise<Team[]> {
        this._store.dispatch(new FetchTeamChildren(id));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchTeamChildrenSuccess),
                map((action: FetchTeamChildrenSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchTeamChildrenFailure),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchTeamParent(id: string): Promise<Team> {
        this._store.dispatch(new FetchTeamParent(id));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchTeamParentSuccess),
                map((action: FetchTeamParentSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchTeamParentFailure),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async createTeam(team: Team): Promise<boolean> {
        this._store.dispatch(new CreateTeam(team));

        this._action$.pipe(ofActionDispatched(CreateTeamSuccess), take(1)).subscribe((action: CreateTeamSuccess) => {
            this._newTeamSubject.next(action.payload);
        });

        return await this._actionHelper.race(CreateTeamSuccess, CreateTeamFailure).toPromise();
    }

    async fetchTeamWithSubTeamsProgress(teamId: string, includeSubTeamsProgress: boolean): Promise<Team[]> {
        this._store.dispatch(new FetchTeamWithSubTeamsProgress({ teamId, includeSubTeamsProgress }));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchTeamWithSubTeamsProgressSuccess),
                map((action: FetchTeamWithSubTeamsProgressSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchTeamWithSubTeamsProgressFailure),
                map((action: FetchTeamWithSubTeamsProgressFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchTeamLeaderTeams(): Promise<boolean> {
        this._store.dispatch(new FetchTeamLeaderTeams());

        return await this._actionHelper.race(FetchTeamLeaderTeamsSuccess, FetchTeamLeaderTeamsFailure).toPromise();
    }

    async fetchTeamSubTeams(teamId: string, page: number = 1, limit: number = 5, query: string = '', append: boolean = false): Promise<Team[]> {
        this._store.dispatch(new FetchTeamSubTeams({ teamId, page, limit, query, append }));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchTeamSubTeamsSuccess),
                map((action: FetchTeamSubTeamsSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchTeamSubTeamsFailure),
                map((action: FetchTeamSubTeamsFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchSubTeamWithSubteamsProgress(teamId: string, includeSubTeamsProgress: boolean): Promise<Team[]> {
        this._store.dispatch(new FetchSubTeamWithSubteamsProgress({ teamId, includeSubTeamsProgress }));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchSubTeamWithSubteamsProgressSuccess),
                map((action: FetchSubTeamWithSubteamsProgressSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchSubTeamWithSubteamsProgressFailure),
                map((action: FetchSubTeamWithSubteamsProgressFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchUserTeams(userId: string): Promise<UserTeam[]> {
        this._store.dispatch(new FetchUserTeams({ userId }));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchUserTeamsSuccess),
                map((action: FetchUserTeamsSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchUserTeamsFailure),
                map((action: FetchUserTeamsFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchUserTeamsV1(userId: string, role: string): Promise<UserTeam[]> {
        this._store.dispatch(new FetchUserTeamsV1({ userId, role }));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchUserTeamsV1Success),
                map((action: FetchUserTeamsV1Success) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchUserTeamsV1Failure),
                map((action: FetchUserTeamsV1Failure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchTeamUsers(teamId: string, selectedTeamId: string, role: string): Promise<User[]> {
        this._store.dispatch(new FetchTeamUsers({ teamId, selectedTeamId, role }));

        const success$ = this._action$.pipe(
            ofActionDispatched(FetchTeamUsersSuccess),
            map((action: FetchTeamUsersSuccess) => action.payload)
        );

        const failure$ = this._action$.pipe(
            ofActionDispatched(FetchTeamUsersFailure),
            map(() => null) // return null on failure
        );

        return firstValueFrom(race(success$, failure$));
    }

    async updateTeam(team: Team): Promise<boolean> {
        this._store.dispatch(new UpdateTeam(team));

        return await this._actionHelper.race(UpdateTeamSuccess, UpdateTeamFailure).toPromise();
    }

    deleteTeam(courseStructureCategoryId: string) {
        this._store.dispatch(new DeleteTeam(courseStructureCategoryId));

        return race(
            this._action$.pipe(
                ofActionDispatched(DeleteTeamSuccess),
                map((action: DeleteTeamSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(DeleteTeamFailure),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    removeTeamFromTree(team: Team) {
        this._teamRemovedSubject.next(team);
    }

    collapseAllTeams(): void {
        this._collapseAllTeamsSubject.next();
    }

    async searchTeams(search: string, pageSize: number, pageNumber: number): Promise<TeamTree[]> {
        this._store.dispatch(new SearchTeams({ search, pageSize, pageNumber }));

        return race(
            this._action$.pipe(
                ofActionDispatched(SearchTeamsSuccess),
                map((action: SearchTeamsSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(SearchTeamsFailure),
                map((action: SearchTeamsFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    searchTree(teamsTree, matchingId: string) {
        if (teamsTree.id == matchingId) {
            return teamsTree;
        } else if (teamsTree.children != null) {
            var i;
            var result = null;
            for (i = 0; result == null && i < teamsTree.children.length; i++) {
                result = this.searchTree(teamsTree.children[i], matchingId);
            }
            return result;
        }
        return null;
    }

    removeFromNestedTree(team: Team, parentId: string, matchingId?: string): Team {
        if (team.id === parentId) {
            team.children = team.children?.filter((childFolder) => childFolder.id !== matchingId);
            return team;
        } else if (team.children != null) {
            var i;
            var result = null;
            for (i = 0; result == null && i < team.children.length; i++) {
                result = this.searchTree(team.children[i], parentId);
                if (result) {
                    this.removeFromNestedTree(result, parentId, matchingId);
                }
            }
            return result;
        }
        return null;
    }

    searchTreeName(courseStructureCategoriesTree, matchingId: string, names: string = ''): string {
        if (courseStructureCategoriesTree.id == matchingId) {
            names = names + '/' + courseStructureCategoriesTree.name;
            names = names.substring(1); // removes first '/' charachter
            return names.substring(names.indexOf('/') + 1); // removes default audience from a path
        } else if (courseStructureCategoriesTree.children != null) {
            var i;
            var result = null;
            names = names + '/' + courseStructureCategoriesTree.name;
            for (i = 0; result == null && i < courseStructureCategoriesTree.children.length; i++) {
                result = this.searchTreeName(courseStructureCategoriesTree.children[i], matchingId, names);
            }
            return result;
        }
        return null;
    }

    mapTeamToEntityNode(team: Team): IEntityNode {
        return {
            id: team.id,
            name: team.name,
            children: [],
            childrenCount: team.subteamCount || 0,
            parent: team.parent ? { id: team.parent.id, name: team.parent.name } : undefined,
            isSelected: false,
        };
    }

    convertListToTree(list: Team[]): Team[] {
        list = list.map((team) => ({
            ...team,
            children: [], // safely initialize children on a new object
        }));

        const map: { [id: number]: number } = {}; // Using an index signature for the map
        let roots: Team[] = [];

        let node: Team, i: number;

        for (i = 0; i < list.length; i += 1) {
            map[list[i].id] = i; // initialize the map
        }

        for (i = 0; i < list.length; i += 1) {
            node = list[i];
            if (node.parent && map[node.parent.id] !== undefined) {
                // If the parent exists in the map, add the node as a child
                list[map[node.parent.id]].children.push(node);
            } else {
                // Otherwise, it's a root node
                roots.push(node);
            }
        }

        this._sortTreeNodes(roots);

        return roots;
    }

    private _sortTreeNodes(nodes: Team[]): void {
        nodes.sort(MaxBrainUtils.sortCompareFn(TeamState.sortProperties));

        nodes.forEach((node) => {
            if (node.children?.length) {
                this._sortTreeNodes(node.children);
            }
        });
    }

    /* Team Members */
    addUsersToTeamAsMembers(payload: IAddTeamMembersActionPayload) {
        this._store.dispatch(new AddUsersToTeamsAsMembers(payload));

        return race(
            this._action$.pipe(
                ofActionDispatched(AddUsersToTeamsAsMembersSuccess),
                map((action: AddUsersToTeamsAsMembersSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(AddUsersToTeamsAsMembersFailure),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    removeTeamMembers(payload: IRemoveTeamMembersActionPayload) {
        this._store.dispatch(new RemoveTeamMembers(payload));

        return race(
            this._action$.pipe(
                ofActionDispatched(RemoveTeamMembersSuccess),
                map((action: RemoveTeamMembersSuccess) => true)
            ),
            this._action$.pipe(
                ofActionDispatched(RemoveTeamMembersFailure),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchTeamMembers(teamId: string, page: number = 1, limit: number = 6, append: boolean = false): Promise<boolean> {
        this._store.dispatch(new FetchTeamMembers({ teamId, page, limit, append }));

        return await this._actionHelper.race(FetchTeamMembersSuccess, FetchTeamMembersFailure).toPromise();
    }

    fetchTeamMembersUsers(teamId: string, includeSubteams: boolean = false): Promise<GlobalParticipant[]> {
        this._store.dispatch(new FetchTeamMembersUsers({ teamId, includeSubteams }));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchTeamMembersUsersSuccess),
                map((action: FetchTeamMembersUsersSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchTeamMembersUsersFailure),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    /* Team Leaders */
    addUsersToTeamAsLeaders(payload: IAddTeamLeadersActionPayload): Promise<User[]> {
        this._store.dispatch(new AddUsersToTeamsAsLeaders(payload));

        return race(
            this._action$.pipe(
                ofActionDispatched(AddUsersToTeamsAsLeadersSuccess),
                map((action: AddUsersToTeamsAsLeadersSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(AddUsersToTeamsAsLeadersFailure),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    removeTeamLeaders(payload: IRemoveTeamLeadersActionPayload) {
        this._store.dispatch(new RemoveTeamLeaders(payload));

        return race(
            this._action$.pipe(
                ofActionDispatched(RemoveTeamLeadersSuccess),
                map((action: RemoveTeamLeadersSuccess) => true)
            ),
            this._action$.pipe(
                ofActionDispatched(RemoveTeamLeadersFailure),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchTeamLeaders(teamId: string): Promise<boolean> {
        this._store.dispatch(new FetchTeamLeaders(teamId));

        return await this._actionHelper.race(FetchTeamLeadersSuccess, FetchTeamLeadersFailure).toPromise();
    }

    /* Team Watchers */
    addUsersToTeamAsWatchers(payload: IAddTeamWatchersActionPayload): Promise<boolean> {
        this._store.dispatch(new AddUsersToTeamsAsWatchers(payload));

        return race(
            this._action$.pipe(
                ofActionDispatched(AddUsersToTeamsAsWatchersSuccess),
                map((action: AddUsersToTeamsAsWatchersSuccess) => true)
            ),
            this._action$.pipe(
                ofActionDispatched(AddUsersToTeamsAsWatchersFailure),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    removeTeamWatchers(payload: IRemoveTeamWatchersActionPayload): Promise<boolean> {
        this._store.dispatch(new RemoveTeamWatchers(payload));

        return race(
            this._action$.pipe(
                ofActionDispatched(RemoveTeamWatchersSuccess),
                map((action: RemoveTeamWatchersSuccess) => true)
            ),
            this._action$.pipe(
                ofActionDispatched(RemoveTeamWatchersFailure),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchTeamWatchers(teamId: string): Promise<boolean> {
        this._store.dispatch(new FetchTeamWatchers(teamId));

        return await this._actionHelper.race(FetchTeamWatchersSuccess, FetchTeamWatchersFailure).toPromise();
    }

    async stopWatchingTeam(teamId: string): Promise<boolean> {
        this._store.dispatch(new StopWatchingTeam(teamId));

        return await this._actionHelper.race(StopWatchingTeamSuccess, StopWatchingTeamFailure).toPromise();
    }

    /* Team */
    unsetTeam(): void {
        this._store.dispatch(new UnsetTeam());
    }
}
